import * as React from 'react';
import { GRID_CHECKBOX_SELECTION_FIELD } from '@mui/x-data-grid';
import {
  DataGridPro,
  GridColDef,
  GridToolbarContainer,
  GridColumnMenuProps,
  GridColumnMenu,
  GridLoadingOverlayProps,
  LoadingOverlayPropsOverrides,
  GridRenderCellParams,
  GridRowSelectionModel,
  useGridApiRef,
} from '@mui/x-data-grid-pro';
import { CircularProgress, LinearProgress } from '@mui/material';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import {
  searchPurchaseOrders,
  setSearchInput,
  setSearchLoading,
  setSelectedPurchaseOrders,
} from '../../../../state/purchaseOrderSearchModalSlice';
import { useDispatch, useSelector } from '../../../../state';
import type { PurchaseOrderSearchInput } from '@repo/api-contract';
import { useCallback } from 'react';
import { debounce } from '@mui/material/utils';
import SearchByDropdown from './SearchByDropdown';
import { useTranslation } from 'react-i18next';
import hexToRGBA from '../../../../util/converters/hexToRGBA';
import { initialPageSize } from './constants';
import {
  EMPTY_LEADING_GRID_COLUMNS,
  GRID_CHECKBOX_COLUMN_ORDER,
  usePersistedDataGridColumnPreferences,
} from '../../../../util/hooks';
import type { DataGridTableId } from '../../../../util/dataGridTableIds';
import { DataGridTranslatedColumnHeader } from '../../../dataGrid/DataGridTranslatedColumnHeader';

const TABLE_ID = 'search-purchase-orders' satisfies DataGridTableId;
const KEY_PO_BROWSE = 'team.Components.PurchaseOrderBrowseTable';

interface LoaderProps
  extends Omit<GridLoadingOverlayProps, 'color'>,
    LoadingOverlayPropsOverrides {
  color?: string;
}

const Loader = (props: LoaderProps) => {
  return <LinearProgress color={'primary'} />;
};

function CustomToolbar({ pageSize }: { pageSize: number }) {
  const dispatch = useDispatch();
  const { t: tMP } = useTranslation([], {
    keyPrefix: 'team.Modals.PurchaseOrdersSearch',
  });
  const { searchInput, searchBy, searchLoading, resp } = useSelector(
    (store) => store.purchaseOrderSearchModalSlice,
  );
  const { team } = useSelector((store) => store.userSlice);

  const debouncedSearch = useCallback(
    debounce((searchQuery: string) => {
      const payload: PurchaseOrderSearchInput = {
        query: searchQuery,
        searchBy,
        size: pageSize,
        page: 0,
      };
      dispatch(searchPurchaseOrders(payload));
    }, 1500),
    [searchBy],
  );

  if (team === 'init' || team === 'loading') return null;

  return (
    <GridToolbarContainer>
      <SearchByDropdown />
      <TextField
        value={searchInput}
        onChange={(e) => {
          dispatch(setSearchLoading(true));
          dispatch(setSearchInput(e.currentTarget.value));
          debouncedSearch(e.currentTarget.value);
        }}
        id="filled-basic"
        label={tMP('Search for Purchase Orders...')}
        variant="outlined"
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchOutlinedIcon />
            </InputAdornment>
          ),
          endAdornment: (
            <React.Fragment>
              {searchLoading ? (
                <CircularProgress color="inherit" size={20} />
              ) : null}
            </React.Fragment>
          ),
        }}
      />
    </GridToolbarContainer>
  );
}

function CustomColumnMenu(props: GridColumnMenuProps) {
  return (
    <GridColumnMenu
      {...props}
      slots={{
        columnMenuColumnsPurchaseOrder: null,
        columnMenuFilterPurchaseOrder: null,
      }}
    />
  );
}

interface TableProps {
  multiSelect: boolean;
}

export default function Table({ multiSelect }: TableProps) {
  const apiRef = useGridApiRef();
  const dispatch = useDispatch();
  const { resp, searchLoading, searchInput, searchBy } = useSelector(
    (store) => store.purchaseOrderSearchModalSlice,
  );

  // 2) Handle row selection
  const handleSelectionChange = (newSelectionModel: GridRowSelectionModel) => {
    // If single selection is desired but newSelectionModel has >1 items,
    // keep only the last item.
    let finalSelectionModel = newSelectionModel;
    if (!multiSelect && newSelectionModel.length > 1) {
      const lastId = newSelectionModel[newSelectionModel.length - 1];
      if (lastId !== undefined) {
        finalSelectionModel = [lastId];
      }
    }

    // Filter the rows to get the full objects of the selected purchaseOrders
    const rows = resp ? resp.results : [];
    const selected = rows.filter((row) =>
      finalSelectionModel.includes(row.PurchaseOrderId),
    );
    dispatch(setSelectedPurchaseOrders(selected));
  };

  const baseColumns: GridColDef[] = React.useMemo(
    () => [
      {
        field: 'code',
        headerName: 'Purchase Order No.',
        type: 'string',
        flex: 1,
        valueGetter: (value, row) => {
          return row.code;
        },
        renderHeader: (params) => (
          <DataGridTranslatedColumnHeader
            labelKey="Purchase Order No."
            keyPrefix={KEY_PO_BROWSE}
            params={params}
          />
        ),
        renderCell: (params: GridRenderCellParams<any>) => (
          <span>{params.value}</span>
        ),
      },
      {
        field: 'vendorName',
        headerName: 'Vendor Name',
        flex: 1,
        valueGetter: (value, row) => {
          return row.vendorName;
        },
        renderHeader: (params) => (
          <DataGridTranslatedColumnHeader
            labelKey="Vendor Name"
            keyPrefix={KEY_PO_BROWSE}
            params={params}
          />
        ),
        renderCell: (params: GridRenderCellParams<any>) => (
          <span>{params.value}</span>
        ),
      },
    ],
    [],
  );

  const persistedPrefs = usePersistedDataGridColumnPreferences({
    tableId: TABLE_ID,
    columns: baseColumns,
    apiRef,
    leadingGridColumnFields: multiSelect
      ? GRID_CHECKBOX_COLUMN_ORDER
      : EMPTY_LEADING_GRID_COLUMNS,
  });

  const columns = React.useMemo(
    () => persistedPrefs.applyPersistedPreferences(baseColumns),
    [baseColumns, persistedPrefs.applyPersistedPreferences],
  );

  return (
    <DataGridPro
      apiRef={apiRef}
      columnHeaderHeight={56}
      sx={{
        border: 'none',
        padding: '1rem',
        '.MuiDataGrid-row:nth-of-type(even):not(.Mui-selected)': {
          backgroundColor: '#ECECEC',
        },
        '.MuiDataGrid-row.Mui-selected': {
          backgroundColor: (theme) =>
            hexToRGBA(theme.palette.primary.main, 0.19),
        },
        '.MuiDataGrid-row.Mui-selected:hover': {
          backgroundColor: (theme) =>
            hexToRGBA(theme.palette.primary.main, 0.19),
        },
        '.MuiDataGrid-scrollbar--horizontal': { display: 'none' },
        '.MuiDataGrid-columnHeaderTitle': {
          fontWeight: 'bold',
          whiteSpace: 'normal',
          lineHeight: 1.2,
        },
        '& .MuiDataGrid-cell:focus-within, & .MuiDataGrid-cell:focus': {
          outline: 'none !important',
        },
      }}
      rows={
        resp
          ? resp.results.map((purchaseOrder) => ({
              ...purchaseOrder,
              id: purchaseOrder.PurchaseOrderId,
            }))
          : []
      }
      onRowSelectionModelChange={handleSelectionChange}
      checkboxSelection={multiSelect} // show checkboxes only if multiSelect=true
      disableRowSelectionOnClick={multiSelect ? true : false}
      loading={searchLoading}
      columns={columns}
      columnVisibilityModel={persistedPrefs.columnVisibilityModel}
      onColumnVisibilityModelChange={
        persistedPrefs.onColumnVisibilityModelChange
      }
      onColumnWidthChange={persistedPrefs.onColumnWidthChange}
      onColumnOrderChange={persistedPrefs.onColumnOrderChange}
      pagination
      paginationMode="server"
      paginationModel={{
        page: resp?.page || 0,
        pageSize: resp?.size || 0,
      }}
      rowCount={resp?.total || 0}
      pageSizeOptions={[initialPageSize, 25, 50, 100]}
      onPaginationModelChange={(newModel) => {
        const payload: PurchaseOrderSearchInput = {
          query: searchInput,
          searchBy,
          page: newModel.page,
          size: newModel.pageSize,
        };
        dispatch(searchPurchaseOrders(payload));
      }}
      pinnedColumns={
        multiSelect ? { left: [GRID_CHECKBOX_SELECTION_FIELD] } : undefined
      }
      disableColumnMenu
      autoHeight={false}
      slots={{
        loadingOverlay: Loader,
        toolbar: () => (
          <CustomToolbar pageSize={resp?.size || initialPageSize} />
        ),
        columnMenu: CustomColumnMenu,
      }}
    />
  );
}
